import { useEffect, Suspense, lazy, type CSSProperties } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import Layout from './components/Layout'
import SiteSeo from './components/SiteSeo'
import GoAffProTracker from './components/GoAffProTracker'
import LoadingSpinner from './components/LoadingSpinner'
import { WorkspaceShellSkeleton } from './components/Skeleton'
import ProtectedRoute from './components/ProtectedRoute'
import RootEntry from './components/RootEntry'
import StudentHumanLayerRoute from './components/StudentHumanLayerRoute'
import HumanLayerPortalRoute from './components/HumanLayerPortalRoute'
import LoginRedirect from './components/LoginRedirect'
import { FeatureGuard } from './config/features'
import { captureReferralCodeFromUrl } from './services/referralService'
import { isStudentWorkspacePath } from './utils/studentWorkspacePath'

/** Lazy-route fallback: shell skeleton on student workspace, spinner elsewhere. */
function AppRouteFallback() {
  const { pathname } = useLocation()
  if (isStudentWorkspacePath(pathname)) {
    return <WorkspaceShellSkeleton />
  }
  /* Marketing blog/updates: cream skeleton instead of bare white + spinner */
  if (pathname === '/blog' || pathname.startsWith('/blog/') || pathname === '/updates' || pathname.startsWith('/updates/')) {
    const line = (width: string, marginTop = 10): CSSProperties => ({
      height: 12,
      width,
      marginTop,
      borderRadius: 6,
      background: 'linear-gradient(90deg, #ebe7df 0%, #f5f2eb 50%, #ebe7df 100%)',
    })
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #faf9f6 0%, #f3f1ec 100%)',
          padding: '28px 28px 80px',
          boxSizing: 'border-box',
        }}
        aria-busy="true"
        aria-label="Loading"
      >
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={line('88px', 0)} />
          <div style={line('70%', 14)} />
          <div style={line('100%', 22)} />
          <div style={line('92%', 10)} />
          <div style={line('64%', 10)} />
        </div>
      </div>
    )
  }
  return <LoadingSpinner />
}

function lazyRetry<T>(factory: () => Promise<T>): Promise<T> {
  return factory().catch(() => factory())
}

// Lazy load all pages to preserve exact functionality
/** Marketing home when signed-in users need plans/subscribe (bare `/` goes to workspace). */
const Home = lazy(() => import('./pages/Home'))
const GoogleAuthCallback = lazy(() => import('./pages/GoogleAuthCallback'))
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'))
const TutorLogin = lazy(() => import('./pages/TutorLogin'))
const TutorRegister = lazy(() => import('./pages/TutorRegister'))
const SchoolLogin = lazy(() => import('./pages/SchoolLogin'))
const SchoolRegister = lazy(() => import('./pages/SchoolRegister'))
const InstructorDashboard = lazy(() => import('./pages/InstructorDashboard'))
const Messages = lazy(() => import('./pages/Messages'))
const MyTutor = lazy(() => import('./pages/MyTutor'))
const Overview = lazy(() => import('./pages/Overview'))
const SchoolDashboard = lazy(() => import('./pages/SchoolDashboard'))
const SchoolProfile = lazy(() => import('./pages/SchoolProfile'))
const TutorProfile = lazy(() => import('./pages/TutorProfile'))
const AccountSettings = lazy(() => import('./pages/AccountSettings'))
const SearchResults = lazy(() => import('./pages/SearchResults'))
const TutoringSession = lazy(() => import('./pages/TutoringSession'))
const CourseMaterialsRedirect = lazy(() => import('./components/CourseMaterialsRedirect'))
const MyPayments = lazy(() => import('./pages/MyPayments'))
const Admin = lazy(() => import('./pages/Admin'))
const AdminLogin = lazy(() => import('./pages/AdminLogin'))
const AdminUsers = lazy(() => import('./pages/AdminUsers'))
const AdminPlatform = lazy(() => import('./pages/AdminPlatform'))
const AdminAiSettings = lazy(() => import('./pages/AdminAiSettings'))
const AdminFinancial = lazy(() => import('./pages/AdminFinancial'))
const AdminLogs = lazy(() => import('./pages/AdminLogs'))
const AdminSessions = lazy(() => import('./pages/AdminSessions'))
const AdminLiveSessionDetail = lazy(() => import('./pages/AdminLiveSessionDetail'))
const PartnersReferral = lazy(() => import('./pages/PartnersReferral'))
const AffiliateDashboard = lazy(() => import('./pages/AffiliateDashboard'))
const AffiliateLogin = lazy(() => import('./pages/AffiliateLogin'))
const AffiliateRegister = lazy(() => import('./pages/AffiliateRegister'))
const AdminWrittenExplanationDetail = lazy(() => import('./pages/AdminWrittenExplanationDetail'))
const TutorEarnings = lazy(() => import('./pages/TutorEarnings'))
const TutorBookings = lazy(() => import('./pages/TutorBookings'))
const TutorSessions = lazy(() => import('./pages/TutorSessions'))
const TutorMessages = lazy(() => import('./pages/TutorMessages'))
const TutorProfilePage = lazy(() => import('./pages/TutorProfile'))
const SchoolFinancials = lazy(() => import('./pages/SchoolFinancials'))
const SchoolTutors = lazy(() => import('./pages/SchoolTutors'))
const SchoolBookings = lazy(() => import('./pages/SchoolBookings'))
const SchoolAnalytics = lazy(() => import('./pages/SchoolAnalytics'))
const SchoolSettings = lazy(() => import('./pages/SchoolSettings'))
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'))
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'))
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'))
const SubscriptionCancel = lazy(() => import('./pages/SubscriptionCancel'))
const WrittenExplanationDetails = lazy(() => import('./pages/WrittenExplanationDetails'))
const LegalPage = lazy(() => import('./pages/LegalPage'))
const Support = lazy(() => import('./pages/Support'))
const AdminSupport = lazy(() => import('./pages/AdminSupport'))
const Updates = lazy(() => lazyRetry(() => import('./pages/Updates')))
const UpdateDetail = lazy(() => lazyRetry(() => import('./pages/UpdateDetail')))
const Blog = lazy(() => lazyRetry(() => import('./pages/Blog')))
const BlogPost = lazy(() => lazyRetry(() => import('./pages/BlogPost')))
const AdminUpdates = lazy(() => lazyRetry(() => import('./pages/AdminUpdates')))
const AdminBlog = lazy(() => lazyRetry(() => import('./pages/AdminBlog')))

function App() {
  const location = useLocation()

  // Last-touch: every navigation with ?ref= overwrites localStorage + zimark_ref cookie.
  useEffect(() => {
    captureReferralCodeFromUrl()
  }, [location.search])

  // New routes should open at the top (footer → /partners was keeping home scroll offset).
  // Keep hash-only navigations (e.g. /#plans) alone so in-page anchors still work.
  useEffect(() => {
    if (location.hash) return
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname])

  return (
    <Layout>
      <SiteSeo />
      <GoAffProTracker />
      <Suspense fallback={<AppRouteFallback />}>
        <Routes>
          {/* Always Available Routes */}
          <Route path="/" element={<RootEntry />} />
          <Route path="/home" element={<Home />} />
          <Route path="/terms" element={<LegalPage docId="terms" />} />
          <Route path="/privacy" element={<LegalPage docId="privacy" />} />
          <Route path="/cookies" element={<LegalPage docId="cookies" />} />
          <Route path="/ip-disclaimer" element={<LegalPage docId="ip-disclaimer" />} />
          <Route path="/partners" element={<PartnersReferral />} />
          <Route path="/updates" element={<Updates />} />
          <Route path="/updates/:slug" element={<UpdateDetail />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/affiliate/login" element={
            <LoginRedirect>
              <AffiliateLogin />
            </LoginRedirect>
          } />
          <Route path="/affiliate/register" element={
            <LoginRedirect>
              <AffiliateRegister />
            </LoginRedirect>
          } />
          <Route path="/affiliate" element={
            <ProtectedRoute requiredRole="affiliate" fallbackPath="/affiliate/login">
              <AffiliateDashboard />
            </ProtectedRoute>
          } />
          <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
          <Route path="/auth/verify-email" element={<VerifyEmail />} />

          {/* Support tickets — students + affiliates (role handled inside the page) */}
          <Route path="/support" element={
            <ProtectedRoute fallbackPath="/">
              <Support />
            </ProtectedRoute>
          } />

          <Route path="/tutor" element={
            <HumanLayerPortalRoute>
            <LoginRedirect>
              <TutorLogin />
            </LoginRedirect>
            </HumanLayerPortalRoute>
          } />
          <Route path="/tutor/register" element={
            <HumanLayerPortalRoute>
            <LoginRedirect>
              <TutorRegister />
            </LoginRedirect>
            </HumanLayerPortalRoute>
          } />
          <Route path="/school" element={
            <HumanLayerPortalRoute>
            <LoginRedirect>
              <SchoolLogin />
            </LoginRedirect>
            </HumanLayerPortalRoute>
          } />
          <Route path="/school/register" element={
            <HumanLayerPortalRoute>
            <LoginRedirect>
              <SchoolRegister />
            </LoginRedirect>
            </HumanLayerPortalRoute>
          } />
          
          {/* Feature-Protected Routes - Student Only */}
          <Route path="/tutoring-session" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
            <StudentHumanLayerRoute>
            <FeatureGuard feature="OVERVIEW">
              <Overview />
            </FeatureGuard>
            </StudentHumanLayerRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/course-materials" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
              <CourseMaterialsRedirect />
            </ProtectedRoute>
          } />
          
          <Route path="/my-tutor" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
            <StudentHumanLayerRoute>
            <FeatureGuard feature="MY_TUTOR">
              <MyTutor />
            </FeatureGuard>
            </StudentHumanLayerRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/messages" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
            <FeatureGuard feature="MESSAGES">
              <Messages />
            </FeatureGuard>
            </ProtectedRoute>
          } />

          <Route path="/workspace" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
            <FeatureGuard feature="MESSAGES">
              <Messages />
            </FeatureGuard>
            </ProtectedRoute>
          } />
          
          <Route path="/account-settings" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
            <FeatureGuard feature="ACCOUNT_SETTINGS">
              <AccountSettings />
            </FeatureGuard>
            </ProtectedRoute>
          } />
          
          <Route path="/search-results" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
            <FeatureGuard feature="SEARCH_RESULTS">
              <SearchResults />
            </FeatureGuard>
            </ProtectedRoute>
          } />
          
          <Route path="/session-details" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
            <StudentHumanLayerRoute>
            <FeatureGuard feature="CLASS_VIDEO">
              <TutoringSession />
            </FeatureGuard>
            </StudentHumanLayerRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/my-payments" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
            <StudentHumanLayerRoute>
            <FeatureGuard feature="MY_PAYMENTS">
              <MyPayments />
            </FeatureGuard>
            </StudentHumanLayerRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/school-profile/:schoolId" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
            <StudentHumanLayerRoute>
            <FeatureGuard feature="SCHOOL_PROFILE">
              <SchoolProfile />
            </FeatureGuard>
            </StudentHumanLayerRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/tutor-profile/:tutorId" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
            <StudentHumanLayerRoute>
            <FeatureGuard feature="TUTOR_PROFILE">
              <TutorProfile />
            </FeatureGuard>
            </StudentHumanLayerRoute>
            </ProtectedRoute>
          } />
          
          <Route path="/instructor-dashboard" element={
            <HumanLayerPortalRoute>
            <ProtectedRoute requiredRole="tutor" fallbackPath="/">
            <FeatureGuard feature="INSTRUCTOR_DASHBOARD">
              <InstructorDashboard />
            </FeatureGuard>
            </ProtectedRoute>
            </HumanLayerPortalRoute>
          } />
          
          <Route path="/school-dashboard" element={
            <HumanLayerPortalRoute>
            <ProtectedRoute requiredRole="school" fallbackPath="/school">
              <FeatureGuard feature="SCHOOL_DASHBOARD">
                <SchoolDashboard />
              </FeatureGuard>
            </ProtectedRoute>
            </HumanLayerPortalRoute>
          } />

          {/* Tutor Routes - Tutor Only */}
          <Route path="/tutor/earnings" element={
            <HumanLayerPortalRoute>
            <ProtectedRoute requiredRole="tutor" fallbackPath="/">
              <FeatureGuard feature="INSTRUCTOR_DASHBOARD">
                <TutorEarnings />
              </FeatureGuard>
            </ProtectedRoute>
            </HumanLayerPortalRoute>
          } />
          
          <Route path="/tutor/bookings" element={
            <HumanLayerPortalRoute>
            <ProtectedRoute requiredRole="tutor" fallbackPath="/">
              <FeatureGuard feature="INSTRUCTOR_DASHBOARD">
                <TutorBookings />
              </FeatureGuard>
            </ProtectedRoute>
            </HumanLayerPortalRoute>
          } />
          
                  <Route path="/tutor/sessions" element={
          <HumanLayerPortalRoute>
          <ProtectedRoute requiredRole="tutor" fallbackPath="/">
            <FeatureGuard feature="INSTRUCTOR_DASHBOARD">
              <TutorSessions />
            </FeatureGuard>
          </ProtectedRoute>
          </HumanLayerPortalRoute>
        } />
        
        <Route path="/tutor/messages" element={
          <HumanLayerPortalRoute>
          <ProtectedRoute requiredRole="tutor" fallbackPath="/">
            <FeatureGuard feature="INSTRUCTOR_DASHBOARD">
              <TutorMessages />
            </FeatureGuard>
          </ProtectedRoute>
          </HumanLayerPortalRoute>
        } />

        <Route path="/tutor/profile" element={
          <HumanLayerPortalRoute>
          <ProtectedRoute requiredRole="tutor" fallbackPath="/">
            <FeatureGuard feature="INSTRUCTOR_DASHBOARD">
              <TutorProfilePage />
            </FeatureGuard>
          </ProtectedRoute>
          </HumanLayerPortalRoute>
        } />

                  {/* School Routes - School Only */}
        <Route path="/school/financials" element={
          <HumanLayerPortalRoute>
          <ProtectedRoute requiredRole="school" fallbackPath="/school">
            <FeatureGuard feature="SCHOOL_DASHBOARD">
              <SchoolFinancials />
            </FeatureGuard>
          </ProtectedRoute>
          </HumanLayerPortalRoute>
        } />
        
        <Route path="/school/tutors" element={
          <HumanLayerPortalRoute>
          <ProtectedRoute requiredRole="school" fallbackPath="/school">
            <FeatureGuard feature="SCHOOL_DASHBOARD">
              <SchoolTutors />
            </FeatureGuard>
          </ProtectedRoute>
          </HumanLayerPortalRoute>
        } />

        <Route path="/school/bookings" element={
          <HumanLayerPortalRoute>
          <ProtectedRoute requiredRole="school" fallbackPath="/school">
            <FeatureGuard feature="SCHOOL_DASHBOARD">
              <SchoolBookings />
            </FeatureGuard>
          </ProtectedRoute>
          </HumanLayerPortalRoute>
        } />

        <Route path="/school/analytics" element={
          <HumanLayerPortalRoute>
          <ProtectedRoute requiredRole="school" fallbackPath="/school">
            <FeatureGuard feature="SCHOOL_DASHBOARD">
              <SchoolAnalytics />
            </FeatureGuard>
          </ProtectedRoute>
          </HumanLayerPortalRoute>
        } />

        <Route path="/school/settings" element={
          <HumanLayerPortalRoute>
          <ProtectedRoute requiredRole="school" fallbackPath="/school">
            <FeatureGuard feature="SCHOOL_DASHBOARD">
              <SchoolSettings />
            </FeatureGuard>
          </ProtectedRoute>
          </HumanLayerPortalRoute>
        } />

          {/* Payment Routes - Public */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/subscription/success" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
              <SubscriptionSuccess />
            </ProtectedRoute>
          } />
          <Route path="/subscription/cancel" element={
            <ProtectedRoute requiredRole="student" fallbackPath="/">
              <SubscriptionCancel />
            </ProtectedRoute>
          } />

          {/* Admin Routes - Admin Only */}
          <Route path="/admin" element={
            <LoginRedirect>
              <AdminLogin />
            </LoginRedirect>
          } />
          <Route path="/admin/login" element={
            <LoginRedirect>
              <AdminLogin />
            </LoginRedirect>
          } />
          
          <Route path="/admin/dashboard" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN">
                <Admin />
              </FeatureGuard>
            </ProtectedRoute>
          } />

          <Route path="/admin/users" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN_USERS">
                <AdminUsers />
              </FeatureGuard>
            </ProtectedRoute>
          } />

          <Route path="/admin/support" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN_SUPPORT">
                <AdminSupport />
              </FeatureGuard>
            </ProtectedRoute>
          } />

          <Route path="/admin/updates" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN_CONTENT">
                <AdminUpdates />
              </FeatureGuard>
            </ProtectedRoute>
          } />

          <Route path="/admin/blog" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN_CONTENT">
                <AdminBlog />
              </FeatureGuard>
            </ProtectedRoute>
          } />

          <Route path="/admin/platform" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN_PLATFORM">
                <AdminPlatform />
              </FeatureGuard>
            </ProtectedRoute>
          } />

          <Route path="/admin/ai-settings" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN_AI_SETTINGS">
                <AdminAiSettings />
              </FeatureGuard>
            </ProtectedRoute>
          } />

          <Route path="/admin/financial" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN_FINANCIAL">
                <AdminFinancial />
              </FeatureGuard>
            </ProtectedRoute>
          } />


          <Route path="/admin/logs" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN_LOGS">
                <AdminLogs />
              </FeatureGuard>
            </ProtectedRoute>
          } />

          <Route path="/admin/sessions" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN_SESSIONS">
                <AdminSessions />
              </FeatureGuard>
            </ProtectedRoute>
          } />

          <Route path="/admin/sessions/live/:sessionId" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN_SESSIONS">
                <AdminLiveSessionDetail />
              </FeatureGuard>
            </ProtectedRoute>
          } />

          <Route path="/admin/sessions/written/:sessionId" element={
            <ProtectedRoute requiredRole="admin">
              <FeatureGuard feature="ADMIN_SESSIONS">
                <AdminWrittenExplanationDetail />
              </FeatureGuard>
            </ProtectedRoute>
          } />
          
          {/* Written Explanation Details */}
          <Route path="/written-explanation/:id" element={<WrittenExplanationDetails />} />
        </Routes>
      </Suspense>
    </Layout>
  )
}

export default App

